/**
 * ai-data-sources-block.tsx — Khối "Nguồn dữ liệu bán hàng" (màn Train AI, cột giữa).
 * Sản phẩm / đơn hàng là dữ liệu ĐỒNG BỘ từ bên ngoài (Evotech, Google Sheet…): bot chọn nguồn
 * nào được dùng. Chỉ hiện nguồn đang có dữ liệu; không có nguồn nào → chỉ một dòng gợi ý.
 * "Kết nối nguồn" mở màn Kết nối ứng dụng ở TAB MỚI (màn này giữ nguyên); "Tải lại" chỉ tải lại danh sách nguồn
 * (cũng tự làm mới khi quay lại tab). Logic thuần ở data-sources-lib.ts.
 */
import { AlertTriangle, Plug } from 'lucide-react'
import { useTranslation } from 'react-i18next'
import { Badge } from '@/components/ui/badge'
import { Switch } from '@/components/ui/misc'
import { OpenInNewTab } from '@/components/ai/open-in-new-tab'
import { ReloadButton } from '@/components/ai/reload-button'
import { CONNECT_APPS_PATH } from '@/components/ai/open-target-lib'
import { aiKeys, useAiDataSources, type AiBotToolsJson, type AiDataSource } from '@/hooks/use-ai'
import { BRAND_LOGOS, type BrandKey } from '@/lib/brand-logos'
import {
  currentSelection, toggleSource, setInStockOnly, staleSources, salesSourcesMode, sourceStats, syncedAtLabel,
} from './data-sources-lib'

const LOCAL_ICON: Record<string, string> = { import: '📄', manual: '✍️', api: '🔗' }

function SourceIcon({ s }: { s: AiDataSource }) {
  const logo = (BRAND_LOGOS as Record<string, { src: string }>)[s.key as BrandKey]
  if (logo) return <img src={logo.src} alt="" className="h-5 w-5 shrink-0 rounded object-contain" />
  return <span className="w-5 shrink-0 text-center">{LOCAL_ICON[s.key] ?? '🗂️'}</span>
}

export function DataSourcesBlock({ tools, onChange, canEdit, categoryLimitCount, onOpenCategoryLimit }: {
  tools: AiBotToolsJson | null | undefined
  onChange: (next: AiBotToolsJson) => void
  canEdit: boolean
  /** Số nhóm sản phẩm đang giới hạn (0 = tất cả). */
  categoryLimitCount: number
  onOpenCategoryLimit: () => void
}) {
  const { t } = useTranslation('aiOps')
  const { data, isError } = useAiDataSources()
  const sources = data?.sources
  const mode = isError ? 'hint' : salesSourcesMode(sources)
  if (mode === 'loading') return null
  const actions = (
    <span className="flex flex-wrap items-center gap-1">
      <ReloadButton queryKeys={[aiKeys.dataSources]} what={t('dataSources.reloadWhat')} iconOnly={mode !== 'hint'} />
      <OpenInNewTab to={CONNECT_APPS_PATH} variant="outline" size="sm" title={t('dataSources.connectTitle')}>
        <Plug className="h-3.5 w-3.5" /> {t('dataSources.connect')}
      </OpenInNewTab>
    </span>
  )
  if (mode === 'hint') {
    return (
      <div className="flex flex-wrap items-center gap-2 rounded-md border border-dashed px-2 py-1.5 text-xs text-muted-foreground">
        <Plug className="h-3.5 w-3.5 shrink-0" />
        <span className="min-w-0 flex-1">{isError ? t('dataSources.loadError') : t('dataSources.noSources')}</span>
        {actions}
      </div>
    )
  }

  const list = sources ?? []
  const sel = currentSelection(tools, list)
  const stale = staleSources(tools, list)
  const anyProducts = list.some((s) => s.products)
  const labelOf = (k: string) => list.find((s) => s.key === k)?.label ?? k

  return (
    <div className="space-y-1.5 rounded-md border bg-muted/20 p-2">
      <div className="flex items-center justify-between px-0.5">
        <p className="text-xs font-semibold">🛒 {t('dataSources.title')}</p>
        <span className="flex flex-wrap items-center gap-1">
          {sel.isDefault && <Badge variant="outline" className="px-1.5 text-[10px]">{t('dataSources.usingAll')}</Badge>}
          {actions}
        </span>
      </div>
      <p className="px-0.5 text-[11px] text-muted-foreground">
        {t('dataSources.intro')}
      </p>

      {list.map((s) => {
        const synced = syncedAtLabel(s.lastSyncAt)
        return (
          <div key={s.key} className="rounded-md bg-background px-2 py-1.5">
            <div className="flex items-center gap-2">
              <SourceIcon s={s} />
              <div className="min-w-0 flex-1">
                <p className="truncate text-sm font-medium">{s.label}</p>
                <p className="truncate text-[11px] text-muted-foreground">
                  {sourceStats(s)}{synced ? ` · ${synced}` : ''}
                </p>
              </div>
            </div>
            <div className="mt-1 flex flex-wrap gap-x-3 gap-y-1 pl-7 text-xs">
              {s.products && (
                <label className="flex cursor-pointer items-center gap-1.5">
                  <input
                    type="checkbox"
                    checked={sel.products.includes(s.key)}
                    disabled={!canEdit}
                    onChange={(e) => onChange(toggleSource(tools, list, 'products', s.key, e.target.checked))}
                  />
                  {t('dataSources.advise')}
                </label>
              )}
              {s.orders && (
                <label className="flex cursor-pointer items-center gap-1.5">
                  <input
                    type="checkbox"
                    checked={sel.orders.includes(s.key)}
                    disabled={!canEdit}
                    onChange={(e) => onChange(toggleSource(tools, list, 'orders', s.key, e.target.checked))}
                  />
                  {t('dataSources.lookupOrders')}
                </label>
              )}
            </div>
          </div>
        )
      })}

      {stale.map((st) => (
        <p key={`${st.kind}:${st.key}`} className="flex items-center gap-1.5 px-0.5 text-[11px] text-amber-600">
          <AlertTriangle className="h-3 w-3 shrink-0" />
          {t('dataSources.stale', { label: labelOf(st.key), kind: st.kind === 'products' ? t('dataSources.kindProducts') : t('dataSources.kindOrders') })}
        </p>
      ))}

      {anyProducts && (
        <>
          <label className="flex items-center gap-2 px-0.5 pt-0.5 text-xs">
            <Switch
              checked={tools?.inStockOnly === true}
              onCheckedChange={(v) => onChange(setInStockOnly(tools, v))}
              disabled={!canEdit}
            />
            {t('dataSources.inStockOnly')}
          </label>
          <button
            type="button"
            onClick={onOpenCategoryLimit}
            className="flex w-full items-center gap-2 rounded px-0.5 py-0.5 text-left text-[11px] text-muted-foreground hover:text-primary"
          >
            <span className="min-w-0 flex-1">{t('dataSources.categoryLimit')}</span>
            <Badge variant="outline" className="px-1.5 text-[10px]">
              {categoryLimitCount === 0 ? t('dataSources.allCategories') : t('dataSources.categoryCount', { count: categoryLimitCount })}
            </Badge>
          </button>
        </>
      )}
      {anyProducts && sel.products.length === 0 && (
        <p className="px-0.5 text-[11px] text-muted-foreground">{t('dataSources.noProductSource')}</p>
      )}
    </div>
  )
}
